import _ from 'lodash'
import { BoardColumn, BoardContent, BoardHeader, BoardScrollWrapper } from 'views/components/Board/CardsBoard'
import { PreloadCard, PreloadCount } from 'views/components/Board/Preload'
import { formatQuantity } from 'modules/utils/numbers/formatQuantity'
import { NoDataCard } from 'views/components/Board/NoDataCard'
import { KeyValue } from 'views/components/KeyValue/KeyValue'
import { AntSkeleton, Progress, Text } from '@agro-club/agroclub-shared'
import { Count } from 'views/components/Board/styled'
import { useTranslation } from 'react-i18next'
import { PositionCard } from './PositionCard'
import { FC, memo, useCallback, useEffect, useState } from 'react'
import { UsContractPositionDetailed } from 'modules/domain/usContracts/types'

const PreloadMargin: FC = () => (
  <AntSkeleton.Button active size="small" style={{ height: 20, width: 200, marginTop: 4 }} />
)

type Props = {
  onSelectPosition: (position: UsContractPositionDetailed | null) => void
  selectedPosition: UsContractPositionDetailed | null
  remainingQuantity?: number
  totalProgress: Progress
  borderRadius?: string
  positions?: UsContractPositionDetailed[]
  progress: Progress
  topMargin: number
  isBest?: boolean
  title: string
}

export const PositionColumn: FC<Props> = memo(
  ({
    selectedPosition,
    remainingQuantity,
    totalProgress,
    onSelectPosition,
    borderRadius,
    topMargin,
    progress,
    title,
    positions,
  }) => {
    const { t } = useTranslation('deal')
    const [internalPos, setInternalPos] = useState(selectedPosition)

    const onPosClick = useCallback((position: UsContractPositionDetailed) => setInternalPos({ ...position }), [])

    // we need this to avoid  selectedPosition pass to PositionCard and have less rerenders
    useEffect(() => {
      onSelectPosition(selectedPosition?.id === internalPos?.id ? null : internalPos)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [internalPos, onSelectPosition])

    return (
      <BoardColumn borderRadius={borderRadius}>
        <BoardHeader>
          <Text as="span" typography="titleH4">
            {title}
            {progress === Progress.WORK ? (
              <PreloadCount />
            ) : (
              <Count color={positions?.length ? 'orange' : 'grey'}>{positions?.length}</Count>
            )}
          </Text>
          {totalProgress === Progress.WORK ? (
            <PreloadMargin />
          ) : (
            <KeyValue
              horizontal
              columnGap="small"
              label={t('common:quantity')}
              labelTypo="bodyMedium"
              valueTypo="bodyMedium"
            >
              {formatQuantity(remainingQuantity ?? 0)}
            </KeyValue>
          )}
        </BoardHeader>

        <BoardScrollWrapper topMargin={topMargin}>
          <BoardContent>
            {progress === Progress.WORK ? (
              <PreloadCard />
            ) : positions?.length ? (
              positions.map((item) => (
                <PositionCard
                  key={item.id}
                  position={item}
                  onPositionClick={onPosClick}
                  isSelected={selectedPosition?.id === item.id}
                />
              ))
            ) : (
              <NoDataCard />
            )}
          </BoardContent>
        </BoardScrollWrapper>
      </BoardColumn>
    )
  },
)
